import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { z } from 'zod';
import { apiGetAvailableEventsRequest } from 'src/api_openapi/v2/bet-glider/apiGetAvailableEvents';
import { apiGetSessionRequest } from 'src/api_openapi/v2/bet-glider/apiGetSession';
import { Common } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { AppLayoutsState } from 'src/domains/layouts/state/AppLayouts.state';
import { BreakpointsState } from 'src/domains/layouts/state/breakpointsState/BreakpointsState';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { ExternalApi } from 'src/domains/layouts/state/externalApi/ExternalApi';
import { BetGliderPopup } from 'src/domains/layouts/state/popupState/popupTypes';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { StreamingState } from '../streamingState/StreamingState';
import { RmgStreamMetadataResponseType } from 'src_server/trpc/types/streaming';
import { BetGliderLaunchUrlCache } from 'src/domains/sportsbook/state/betGliderState/BetGliderLaunchUrlCache';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';

export class BetGliderState {
    @observable public url = '';
    @observable public isOpen = false;
    @observable public isIframeLoaded = false;
    @observable private hasTriedLoadingAvailableEvents = false;
    @observable private isLoadingAvailableEvents = false;
    @observable private mappedEvents: Map<string, string> = new Map();
    private readonly streamingState: StreamingState;
    private readonly launchUrlCache: BetGliderLaunchUrlCache;
    public static get = AutoWeakMap.create((common: Common) => new BetGliderState(common));

    private constructor(private readonly common: Common) {
        makeObservable(this);
        this.streamingState = StreamingState.get(common);
        this.launchUrlCache = BetGliderLaunchUrlCache.get(common);

        this.observeViewportChanges();
        this.observeBetGliderEventId();
    }

    public openBetGlider = (race: EventModel): void => {
        StarRouter.get(this.common).setBetGliderEventId(race.id2.toOldId());
    };

    @computed public get isViewportSupported(): boolean {
        if (EnvironmentState.get(this.common).isMobileApp()) {
            return true;
        }

        return BreakpointsState.get(this.common).desktop.isBiggerOrEq === false;
    }

    public getNewRMGStreamMetaData = async (race: EventModel): Promise<RmgStreamMetadataResponseType | null> => {
        const metadataResource = this.streamingState.getModel(race.id).newRMGStreamMetaData;
        let streamResource = metadataResource.get();

        while (streamResource.type === 'loading') {
            await streamResource.whenReady;
            streamResource = metadataResource.get();
        }

        if (streamResource.type === 'ready') {
            return streamResource.value;
        }

        return null;
    };

    public getLaunchUrl = async (race: EventModel): Promise<string | null> => {
        const { gatewayApiHost } = this.common.envVariables;
        const { currentJwt, userId } = this.common.session;

        if (userId === null) {
            return null;
        }

        if (gatewayApiHost === null || gatewayApiHost === '') {
            console.error('Missing gatewayApiHost for bet glider request');
            return null;
        }

        if (currentJwt === null || currentJwt === '') {
            console.error('Missing token for bet glider request');
            return null;
        }

        const eventId = race.id2.toOldId().toString();

        const [response, streamUrl, rmgStreamMetaData] = await Promise.all([
            apiGetSessionRequest(
                gatewayApiHost,
                {
                    requestBody: {
                        accountId: userId,
                        eventId,
                    },
                },
                {
                    authorization: `Bearer ${currentJwt}`,
                }
            ),
            this.streamingState.getModel(race.id).getStreamURL(),
            this.getNewRMGStreamMetaData(race),
        ]);

        if (response.status !== 200) {
            console.error('Bet glider launchUrl request failed', response);
            return null;
        }

        let launchUrl = response.body.launchUrl;

        if (streamUrl !== null) {
            try {
                const url = new URL(launchUrl);
                url.searchParams.set('streamingUrl', streamUrl);
                return url.toString();
            } catch {
                const separator = launchUrl.includes('?') ? '&' : '?';
                launchUrl = `${launchUrl}${separator}streamingUrl=${encodeURIComponent(streamUrl)}`;
            }
        }

        if (rmgStreamMetaData !== null) {
            const encodedRmgStreamMetaData = btoa(JSON.stringify(rmgStreamMetaData));

            try {
                const url = new URL(launchUrl);
                url.searchParams.set('rmgStreamMetaData', encodedRmgStreamMetaData);
                return url.toString();
            } catch {
                const separator = launchUrl.includes('?') ? '&' : '?';
                launchUrl = `${launchUrl}${separator}rmgStreamMetaData=${encodeURIComponent(encodedRmgStreamMetaData)}`;
            }
        }

        return launchUrl;
    };

    public isRaceMapped = (raceId: EventId): boolean => {
        this.ensureAvailableEventsLoaded();

        const eventId = raceId.toOldId().toString();
        return this.mappedEvents.has(eventId);
    };

    @action public close = (): void => {
        this.url = '';
        this.isOpen = false;
        this.isIframeLoaded = false;
        StarRouter.get(this.common).clearBetGliderEventId();
        AppLayoutsState.get(this.common).popupState.hide();
    };

    @action public onIframeLoad = (): void => {
        this.isIframeLoaded = true;
    };

    @action public onIframeMessage = (message: string): void => {
        if (message === 'BACK_ARROW_CLICKED') {
            this.close();
        }
    };

    @action private showLoading = (): void => {
        this.isOpen = true;
        AppLayoutsState.get(this.common).popupState.show(new BetGliderPopup());
    };

    /**
     * iOS WKWebView drops Partitioned session cookies in nested iframes.
     * Open BetGlider top-level in the RN ExternalLinkModal instead (same path as Yaspa etc.).
     */
    @action private show = (url: string): void => {
        if (EnvironmentState.get(this.common).isAppIOSWrapper()) {
            this.openInIosAppModal(url);
            return;
        }

        this.url = url;
        this.isIframeLoaded = false;
        this.isOpen = true;
        AppLayoutsState.get(this.common).popupState.show(new BetGliderPopup());
    };

    @action private openInIosAppModal = (url: string): void => {
        ExternalApi.get(this.common).openLinkInModal(url);
        this.url = '';
        this.isOpen = false;
        this.isIframeLoaded = false;
        StarRouter.get(this.common).clearBetGliderEventId();
        AppLayoutsState.get(this.common).popupState.hide();
    };

    @action private closeOnFailure = (): void => {
        this.url = '';
        this.isOpen = false;
        this.isIframeLoaded = false;
        StarRouter.get(this.common).clearBetGliderEventId();
        AppLayoutsState.get(this.common).popupState.hide();
    };

    private observeViewportChanges = (): void => {
        reaction(
            () => ({
                hasOpenBetGlider: this.isOpen,
                isViewportSupported: this.isViewportSupported,
            }),
            ({ hasOpenBetGlider, isViewportSupported }) => {
                if (hasOpenBetGlider && isViewportSupported === false) {
                    this.close();
                }
            }
        );
    };

    private observeBetGliderEventId = (): void => {
        reaction(
            () => ({
                eventId: StarRouter.get(this.common).betGliderEventIdParam,
                userId: this.common.session.userId,
                isViewportSupported: this.isViewportSupported,
                isEnabled: ConfigComponents.get(this.common).config.isBetGliderActive,
                hasOpenBetGlider: this.isOpen,
            }),
            ({ eventId, userId, isViewportSupported, isEnabled, hasOpenBetGlider }) => {
                void this.openFromEventId(eventId, userId, isViewportSupported, isEnabled, hasOpenBetGlider);
            },
            { fireImmediately: true }
        );
    };

    private openFromEventId = async (
        eventId: number | null,
        userId: number | null,
        isViewportSupported: boolean,
        isEnabled: boolean,
        hasOpenBetGlider: boolean
    ): Promise<void> => {
        const starRouter = StarRouter.get(this.common);

        if (eventId === null) {
            if (hasOpenBetGlider) {
                this.close();
            }
            return;
        }

        if (hasOpenBetGlider) {
            return;
        }

        if (isViewportSupported === false || isEnabled === false) {
            starRouter.clearBetGliderEventId();
            return;
        }

        if (userId === null) {
            return;
        }

        const universe = this.common.envVariables.universe;
        const cachedUrl = this.launchUrlCache.get(universe, userId, eventId);

        if (cachedUrl === null) {
            this.showLoading();
        }

        const race = await this.common.models.id.getEventId(eventId).getEventModelAsync();

        if (race === null || race.sport !== 'horseracing' || starRouter.betGliderEventIdParam !== eventId) {
            this.closeOnFailure();
            return;
        }

        if (cachedUrl !== null) {
            if (starRouter.betGliderEventIdParam !== eventId) {
                this.closeOnFailure();
                return;
            }

            this.show(cachedUrl);
            return;
        }

        const launchUrl = await this.getLaunchUrl(race);

        if (starRouter.betGliderEventIdParam !== eventId) {
            this.closeOnFailure();
            return;
        }

        if (launchUrl === null) {
            this.closeOnFailure();
            return;
        }

        this.launchUrlCache.set(universe, userId, eventId, launchUrl);
        this.show(launchUrl);
    };

    private ensureAvailableEventsLoaded = (): void => {
        if (this.hasTriedLoadingAvailableEvents || this.isLoadingAvailableEvents) {
            return;
        }

        runInAction(() => {
            this.hasTriedLoadingAvailableEvents = true;
            this.isLoadingAvailableEvents = true;
        });

        void this.loadAvailableEvents();
    };

    private loadAvailableEvents = async (): Promise<void> => {
        const { gatewayApiHost } = this.common.envVariables;

        const config = ConfigComponents.get(this.common);
        if (config.config.isBetGliderActive === false) {
            runInAction(() => {
                this.isLoadingAvailableEvents = false;
            });
            return;
        }

        if (gatewayApiHost === null || gatewayApiHost === '') {
            console.error('Missing gatewayApiHost for bet glider available events request');
            runInAction(() => {
                this.isLoadingAvailableEvents = false;
            });
            return;
        }

        const authorization = this.common.session.currentJwt;

        try {
            const response = await apiGetAvailableEventsRequest(
                gatewayApiHost,
                {},
                {
                    authorization:
                        authorization !== null && authorization !== '' ? `Bearer ${authorization}` : undefined,
                }
            );

            if (response.status !== 200) {
                console.error('Bet glider available events request failed', response);
                return;
            }

            const parsedMapping = z.record(z.string()).safeParse(response.body.value);

            if (!parsedMapping.success) {
                console.error('Bet glider available events payload is invalid', {
                    errors: parsedMapping.error.errors,
                    data: response.body.value,
                });
                return;
            }

            runInAction(() => {
                this.mappedEvents.clear();

                for (const [eventId, mappedEventId] of Object.entries(parsedMapping.data)) {
                    this.mappedEvents.set(eventId, mappedEventId);
                }
            });
        } catch (error) {
            console.error('Bet glider available events request error', error);
        } finally {
            runInAction(() => {
                this.isLoadingAvailableEvents = false;
            });
        }
    };
}
